import { t } from '@/features/i18n/utils';
import { SuspenseBoundary } from '@/ui/components/suspense-boundary';
import { ProcurementBreadcrumbs } from '@/features/subcontractor-bids/components/procurement-breadcrumbs';
import { createFileRoute, Link } from '@tanstack/react-router';
import { noop, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { Icon } from '@/ui/components/icon';
import {
  AlertCircleIcon,
  ArrowDownIcon,
  ArrowLeft01Icon,
  ArrowRight01Icon,
  FolderIcon,
  LinkSquare02Icon,
  PlusSignIcon,
  Upload01Icon,
  AuctionIcon,
  SettingsIcon,
} from '@hugeicons-pro/core-stroke-rounded';
import { Header, HeaderBackButton, HeaderChatButton } from '@/ui/components/header';
import { Alert, AlertDescription, AlertTitle } from '@/ui/components/alert';
import {
  MainAlert,
  MainHeader,
  MainHeaderBackButton,
  MainNav,
  MainNavItem,
  MainNavLink,
  MainNavList,
  MainScrollArea,
} from '@/ui/components/main';
import { Button } from '@/ui/components/button';
import { Badge } from '@/ui/components/badge';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyMedia,
  EmptyTitle,
} from '@/ui/components/empty';
import {
  offersQuery,
  procurementAssessmentQuery,
  procurementDetailsQuery,
  procurementItemsQuery,
  subRfpQuery,
} from '@/features/subcontractor-bids/queries';
import { useProcurementIndexingProgress } from '@/features/subcontractor-bids/helpers';
import { BidComparisonCard } from '@/features/subcontractor-bids/components/bid-comparison-card';
import { BidCoverageCard } from '@/features/subcontractor-bids/coverage/components/bid-coverage-card';
import { ReservationsCard } from '@/features/subcontractor-bids/components/reservations-card';
import { entityDocumentsQuery } from '@/features/documents/queries';
import { useSetAtom } from 'jotai';
import { fileUploadOpenAtom } from '@/features/documents/store';
import { FileUploadDialog } from '@/features/documents/components/file-upload';
import { useFilesIndexing } from '@/features/documents/helpers';
import { Tooltip, TooltipPopup, TooltipTrigger } from '@/ui/components/tooltip';
import { IndexingStatusAlerts } from '@/features/documents/components/indexing-status-alerts';
import { CoverageStatusAlert } from '@/features/subcontractor-bids/coverage/components/coverage-status-alert';
import { IndexingRequiredAlert } from '@/features/documents/components/indexing-required-alert';
import { IndexingProgressAlert } from '@/features/documents/components/indexing-progress-alert';
import { createProcurementBidOpenAtom } from '@/features/subcontractor-bids/store';
import { CreateProcurementBid } from '@/features/subcontractor-bids/components/create-procurement-bid';
import { CreateBidComparisonSheet } from '@/features/bid-comparison/components/create-bid-comparison-sheet';
import { WorkPackScopeSection } from '@/features/subcontractor-bids/components/work-pack-scope-section';
import { DocumentsTree } from '@/features/subcontractor-bids/components/documents-tree';
import { Card } from '@/ui/components/card';
import { ProcurementPackSelector } from '@/features/subcontractor-bids/components/procurement-pack-selector';
import {
  Accordion,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
  AccordionTriggerIcon,
} from '@/ui/components/accordion';
import { SubcontractorBidsTable } from '@/features/subcontractor-bids/components/subcontractor-bids-table';
import { Carousel, CarouselContent, CarouselItem, useCarousel } from '@/ui/components/carousel';
import { H1, H3 } from '@/ui/components/heading';
import { DraftScopeOfWorkCard } from '@/features/subcontractor-bids/components/draft-scope-of-work-card';

function AnalysisCarouselNavigation() {
  const { scrollPrev, scrollNext, canScrollPrev, canScrollNext } = useCarousel();

  return (
    <div className="flex items-center">
      <Button variant="ghost" size="sm" kind="icon" disabled={!canScrollPrev} onClick={scrollPrev}>
        <Icon icon={ArrowLeft01Icon} />
        <span className="sr-only">Previous slide</span>
      </Button>
      <Button variant="ghost" size="sm" kind="icon" disabled={!canScrollNext} onClick={scrollNext}>
        <Icon icon={ArrowRight01Icon} />
        <span className="sr-only">Next slide</span>
      </Button>
    </div>
  );
}

export const Route = createFileRoute(
  '/_authed/project/$projectId/workspaces/$workspaceId/subcontractor-bids/$procurementId/',
)({
  component: RouteComponent,
  loader: (opts) => {
    opts.context.queryClient.query(subRfpQuery(opts.params.procurementId)).catch(noop);
    opts.context.queryClient.query(procurementItemsQuery(opts.params.procurementId)).catch(noop);
    opts.context.queryClient
      .query(entityDocumentsQuery(opts.params.procurementId, 'workPackage'))
      .catch(noop);
  },
});

function RouteComponent() {
  const { projectId, workspaceId, procurementId } = Route.useParams();

  const setCreateProcurementBidOpen = useSetAtom(createProcurementBidOpenAtom);
  const setFileUploadOpen = useSetAtom(fileUploadOpenAtom);

  const { data: procurement } = useSuspenseQuery(
    procurementDetailsQuery(workspaceId, procurementId),
  );
  const { data: offers } = useSuspenseQuery(offersQuery(workspaceId, procurementId));

  const { data: assessment } = useQuery(procurementAssessmentQuery(procurementId));

  const {
    progress: indexingProgress,
    isCompleted: indexingCompleted,
    totalDocuments: indexingTotalDocuments,
    isStalled: indexingStalled,
    isKgBuilding: indexingKgBuilding,
  } = useProcurementIndexingProgress();
  const { isFullyIndexed, uploadDisabledTooltip } = useFilesIndexing(procurementId, 'workPackage');

  const sortedDisciplines = procurement.disciplines
    .map((d) => ({ slug: d.slug, label: d.displayName }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <>
      <Header>
        <HeaderBackButton />
        <ProcurementBreadcrumbs />
        <HeaderChatButton />
      </Header>
      <MainScrollArea>
        <MainAlert>
          <IndexingStatusAlerts resourceId={workspaceId} documentType="tenderPackage" waitForKg />
          <CoverageStatusAlert />
          <IndexingRequiredAlert resourceId={procurementId} documentType="workPackage" waitForKg />
          {indexingTotalDocuments > 0 && (
            <IndexingProgressAlert
              isCompleted={indexingCompleted}
              progress={indexingProgress}
              isStalled={indexingStalled}
              isKgBuilding={indexingKgBuilding}
            />
          )}
        </MainAlert>
        <MainHeader className="py-2.5">
          <div className="items-center max-md:hidden flex">
            <MainHeaderBackButton
              className="mr-0"
              to="/project/$projectId/workspaces/$workspaceId/subcontractor-bids"
              params={{ projectId, workspaceId }}
            />
            <SuspenseBoundary
              fallback={
                <Button variant="ghost" size="xl" className="w-full md:w-auto" disabled>
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <H1 className="line-clamp-1">{procurement.displayName}</H1>
                  </div>
                  <Icon icon={ArrowDownIcon} className="ml-auto -mr-0.75 shrink-0 size-4" />
                </Button>
              }
            >
              <ProcurementPackSelector />
            </SuspenseBoundary>
          </div>
          <Button
            className="max-md:w-full"
            variant="secondary"
            kind="with-icon"
            onClick={() => setCreateProcurementBidOpen(true)}
          >
            <Icon icon={PlusSignIcon} />
            {t({ en: 'New supplier bid', nb: 'Nytt leverandørtilbud' })}
          </Button>
        </MainHeader>
        <MainNav className="pb-0! border-b-0!">
          <MainNavList>
            {offers.length > 0 && (
              <MainNavItem>
                <MainNavLink
                  to="/project/$projectId/workspaces/$workspaceId/subcontractor-bids/$procurementId/files"
                  params={{ projectId, workspaceId, procurementId }}
                >
                  <Icon icon={FolderIcon} />
                  {t({ en: 'Files', nb: 'Filer' })}
                </MainNavLink>
              </MainNavItem>
            )}
            <MainNavItem>
              <MainNavLink
                to="/project/$projectId/workspaces/$workspaceId/subcontractor-bids/$procurementId/settings"
                params={{ projectId, workspaceId, procurementId }}
              >
                <Icon icon={SettingsIcon} />
                {t({ en: 'Settings', nb: 'Innstillinger' })}
              </MainNavLink>
            </MainNavItem>
          </MainNavList>
        </MainNav>
        <div className="min-w-0 py-6 flex @4xl:flex-row flex-col gap-4 @5xl:gap-6">
          <div className="min-w-0 w-full">
            <Carousel opts={{ align: 'start' }}>
              <div className="mb-3 flex items-center justify-between px-x @4xl:pr-0 @4xl:pl-x">
                <H3>{t({ en: 'Start an analysis', nb: 'Start en analyse' })}</H3>
                <AnalysisCarouselNavigation />
              </div>
              <CarouselContent className="pb-8">
                <CarouselItem className="ml-x basis-1/4 min-w-84 *:h-full">
                  <ReservationsCard />
                </CarouselItem>
                <CarouselItem className="basis-1/4 min-w-84 *:h-full">
                  <BidCoverageCard />
                </CarouselItem>
                <CarouselItem className="basis-1/4 min-w-84 *:h-full">
                  <BidComparisonCard />
                </CarouselItem>
                <SuspenseBoundary>
                  <CarouselItem className="basis-1/4 min-w-84 *:h-full mr-x @4xl:mr-0">
                    <DraftScopeOfWorkCard />
                  </CarouselItem>
                </SuspenseBoundary>
              </CarouselContent>
            </Carousel>
            <div className="px-x @4xl:pr-0 @4xl:pl-x">
              <H3 className="mb-3">{t({ en: 'Supplier bids', nb: 'Leverandørtilbud' })}</H3>
              <Card className="w-full overflow-hidden relative min-h-80 mb-4">
                {offers.length === 0 ? (
                  <Empty>
                    <EmptyMedia variant="icon">
                      <Icon icon={AuctionIcon} />
                    </EmptyMedia>
                    <EmptyContent>
                      <EmptyTitle>{t({ en: 'Supplier bids', nb: 'Leverandørtilbud' })}</EmptyTitle>
                      <EmptyDescription>
                        {t({
                          en: 'No supplier bids to display. Add your first supplier bid to get started.',
                          nb: 'Ingen leverandørtilbud å vise. Legg til ditt første leverandørtilbud for å komme i gang.',
                        })}
                      </EmptyDescription>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setCreateProcurementBidOpen(true)}
                      >
                        <Icon icon={PlusSignIcon} />
                        {t({ en: 'New supplier bid', nb: 'Nytt leverandørtilbud' })}
                      </Button>
                    </EmptyContent>
                  </Empty>
                ) : (
                  <SubcontractorBidsTable />
                )}
              </Card>
              {assessment && assessment.unattachedOffers.length > 0 && (
                <Alert variant="warning" className="mb-4">
                  <Icon icon={AlertCircleIcon} />
                  <AlertTitle>
                    {t({
                      en: "Some offers aren't attached to this pack",
                      nb: 'Noen tilbud er ikke knyttet til denne pakken',
                    })}
                  </AlertTitle>
                  <AlertDescription>
                    {t({
                      en: `${assessment.unattachedOffers.map((o) => o.companyName).join(', ')} exist on this tender but aren't attached to any procurement pack, so none of the analysis below covers them. Attach them to a pack to include them.`,
                      nb: `${assessment.unattachedOffers.map((o) => o.companyName).join(', ')} finnes på denne konkurransen, men er ikke knyttet til noen anskaffelsespakke, så ingen av analysene nedenfor dekker dem. Knytt dem til en pakke for å inkludere dem.`,
                    })}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </div>
          <Card className="shrink-0 @4xl:mr-x @4xl:px-0 @4xl:max-w-76 sticky px-x top-6 h-fit @9xl:max-w-110 @8xl:max-w-96 w-full">
            <Accordion
              multiple
              className="divide-y"
              defaultValue={['client-rfp', 'add-context', 'disciplines', 'work-pack-scope']}
            >
              <div className="px-3 py-2">
                <div className="flex items-center gap-1.5">
                  <p className="font-medium text-sm">
                    {t({ en: 'Pack baseline', nb: 'Pakkegrunnlag' })}
                  </p>
                  <SuspenseBoundary>
                    <BaselineFileCount />
                  </SuspenseBoundary>
                </div>
                <p className="text-muted text-xs mt-1">
                  {t({
                    en: 'Client RFP files live on the tender. Extra context and disciplines here refine this pack.',
                    nb: 'Klient-RFP-filer ligger på anbudet. Ekstra kontekst og fagområder her presiserer denne pakken.',
                  })}
                </p>
              </div>
              <AccordionItem value="client-rfp" className="px-3 py-3">
                <SuspenseBoundary>
                  <TenderDocumentsTrigger />
                </SuspenseBoundary>
                <AccordionPanel>
                  <DocumentsTree
                    resourceId={workspaceId}
                    documentType="tenderPackage"
                    emptyLabel={t({
                      en: 'No client RFP files on this tender yet. Upload them on the tender files page so analysis can run.',
                      nb: 'Ingen klient-RFP-filer på dette anbudet ennå. Last dem opp på anbudets filside slik at analysen kan kjøre.',
                    })}
                  />
                  <Button
                    variant="secondary"
                    size="xs"
                    className="mt-2"
                    nativeButton={false}
                    render={
                      <Link
                        to="/project/$projectId/workspaces/$workspaceId/files"
                        params={{ projectId, workspaceId }}
                      />
                    }
                  >
                    {t({ en: 'Go to RFP files', nb: 'Gå til RFP-filer' })}
                    <Icon icon={LinkSquare02Icon} className="size-3.5" />
                  </Button>
                </AccordionPanel>
              </AccordionItem>
              <AccordionItem value="add-context" className="px-3 py-3">
                <SuspenseBoundary>
                  <PackDocumentsTrigger />
                </SuspenseBoundary>
                <AccordionPanel>
                  <DocumentsTree
                    resourceId={procurementId}
                    documentType="workPackage"
                    emptyLabel={t({
                      en: 'No extra pack files yet. Client RFP files go on the tender, not here.',
                      nb: 'Ingen ekstra pakkefiler ennå. Klient-RFP-filer hører hjemme på anbudet, ikke her.',
                    })}
                    removable
                  />
                  <Tooltip>
                    <TooltipTrigger
                      delay={0}
                      render={
                        <Button
                          className="mt-4"
                          variant="accent"
                          size="xs"
                          onClick={() => setFileUploadOpen(true)}
                          disabled={!isFullyIndexed}
                        >
                          <Icon icon={Upload01Icon} />
                          {t({ en: 'Add context', nb: 'Legg til kontekst' })}
                        </Button>
                      }
                    />
                    {!isFullyIndexed ? <TooltipPopup>{uploadDisabledTooltip}</TooltipPopup> : null}
                  </Tooltip>
                </AccordionPanel>
              </AccordionItem>
              {sortedDisciplines.length > 0 && (
                <AccordionItem value="disciplines" className="px-3 py-3">
                  <AccordionTrigger>
                    <p className="font-medium text-sm">
                      {t({
                        en: `Disciplines selected (${sortedDisciplines.length})`,
                        nb: `Valgte fagområder (${sortedDisciplines.length})`,
                      })}
                    </p>
                    <AccordionTriggerIcon className="ml-auto" />
                  </AccordionTrigger>
                  <AccordionPanel>
                    <ul className="mt-3 space-y-2.5 list-disc pl-4 marker:text-muted">
                      {sortedDisciplines.map((discipline) => (
                        <li key={discipline.slug} className="text-sm">
                          {discipline.label}
                        </li>
                      ))}
                    </ul>
                  </AccordionPanel>
                </AccordionItem>
              )}
              <SuspenseBoundary>
                <WorkPackScopeSection value="work-pack-scope" />
              </SuspenseBoundary>
            </Accordion>
          </Card>
        </div>
      </MainScrollArea>
      <CreateProcurementBid />
      <CreateBidComparisonSheet />
      <FileUploadDialog resourceId={procurementId} resourceType="workPackage" />
    </>
  );
}

function BaselineFileCount() {
  const { workspaceId, procurementId } = Route.useParams();
  const { data: tenderDocuments } = useSuspenseQuery(
    entityDocumentsQuery(workspaceId, 'tenderPackage'),
  );
  const { data: documents } = useSuspenseQuery(entityDocumentsQuery(procurementId, 'workPackage'));
  return (
    <Badge size="xs" variant="secondary" className="ml-1">
      <Icon icon={FolderIcon} className="size-4" />
      {t({
        en:
          documents.length + tenderDocuments.length === 1
            ? `${documents.length + tenderDocuments.length} file`
            : `${documents.length + tenderDocuments.length} files`,
        nb:
          documents.length + tenderDocuments.length === 1
            ? `${documents.length + tenderDocuments.length} fil`
            : `${documents.length + tenderDocuments.length} filer`,
      })}
    </Badge>
  );
}

function TenderDocumentsTrigger() {
  const { workspaceId } = Route.useParams();
  const { data: documents } = useSuspenseQuery(entityDocumentsQuery(workspaceId, 'tenderPackage'));
  return (
    <AccordionTrigger>
      <p className="font-medium text-sm">
        {t({ en: `Client RFP (${documents.length})`, nb: `Klient-RFP (${documents.length})` })}
      </p>
      <AccordionTriggerIcon className="ml-auto" />
    </AccordionTrigger>
  );
}

function PackDocumentsTrigger() {
  const { procurementId } = Route.useParams();
  const { data: documents } = useSuspenseQuery(entityDocumentsQuery(procurementId, 'workPackage'));
  return (
    <AccordionTrigger>
      <p className="font-medium text-sm">
        {t({
          en: `Additional context (${documents.length})`,
          nb: `Tilleggskontekst (${documents.length})`,
        })}
      </p>
      <AccordionTriggerIcon className="ml-auto" />
    </AccordionTrigger>
  );
}
